import React, {FC, useState} from "react";
import apiService from "../services/api-service";
import {ILoginResult} from "../types";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "./AuthContext";
import Spinner from "./Spinner";
import {getUserRoleFromToken} from "../utils/auth";

export const Login: FC = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLogging, setIsLogging] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {login, saveToken, isLoggedIn, token, userRole} = useAuth();

    const redirectToRolePage = (role: string) => {
        navigate(role === "Scanner" ? "/scanner" : "/participants");
    };

    // Declarative redirect if already logged in
    if (location.pathname === "/login" && isLoggedIn && token && userRole) {
        const redirectPath = userRole === "Scanner" ? "/scanner" : "/participants";
        return <Navigate to={redirectPath} replace/>;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLogging(true);

        try {
            const response = await apiService.loginUser<ILoginResult>({email, password});

            const { token: responseToken} = response.data;

            if (response.status !== 200) {
                alert("Přihlášení nebylo úspěšné. Zkontrolujte prosím e-mail a heslo.");
                setPassword("");
                setIsLogging(false);
                return;
            }

            if (!responseToken) {
                alert("Došlo k chybě. Chybí autentizační token.");
                setPassword("");
                setIsLogging(false);
                return;
            }

            try {
                const role = getUserRoleFromToken(responseToken);
                localStorage.setItem("token", responseToken);
                saveToken(responseToken);
                login();
                redirectToRolePage(role);
            } catch (err) {
                console.error("Token parsing error:", err);
                alert("Přihlášení selhalo. Token není platný.");
                setPassword("");
                setIsLogging(false);
            }
        } catch (error: any) {
            console.error("Login error:", error);
            alert("Nesprávný email nebo heslo.");
            setPassword("");
            setIsLogging(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded shadow-md">
                <h2 className="text-2xl font-bold text-center">Přihlášení</h2>
                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700"
                        >
                            E-mail
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isLogging}
                            className="w-full px-3 py-2 mt-1 border rounded-md shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Heslo
                        </label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isLogging}
                            className="w-full px-3 py-2 mt-1 border rounded-md shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLogging}
                        className={`w-full px-4 py-2 font-medium text-white rounded-md focus:outline-none focus:ring ${
                            isLogging
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-200"
                        }`}
                    >
                        {isLogging ? (
                            <div className="flex items-center justify-center gap-2">
                                <Spinner size={20}/>
                                Přihlašuji...
                            </div>
                        ) : (
                            "Přihlásit"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};
